import { Button } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { type EventInfo } from '../../api/infosService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ServiceInfoCardProps {
  filteredInfos: EventInfo[];
  currentInfo: EventInfo | undefined;
  currentInfoIndex: number;
  isVisible: boolean;
  cardPosition: { x: number; y: number } | null;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onClose: () => void;
  onPrevious: () => void;
  onNext: (maxLength: number) => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
}

export const ServiceInfoCard = ({
  filteredInfos,
  currentInfo,
  currentInfoIndex,
  isVisible,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  onPrevious,
  onNext,
  cardRef,
}: ServiceInfoCardProps) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();

  if (!isVisible || filteredInfos.length === 0 || !currentInfo) return null;

  return (
    <div
      ref={cardRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { bottom: 24, left: 24 }),
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        padding: '12px 14px',
        borderRadius: 10,
        zIndex: isDragging ? 2000 : 1000,
        minWidth: 280,
        maxWidth: 360,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}>
      {/* Header */}
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          cursor: 'grab',
          userSelect: 'none',
        }}>
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Service Info @ {dayjs(currentInfo.value_date).tz(selectedEvent?.circuit?.time_zone || 'UTC').format('HH:mm')}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {filteredInfos.length > 1 && (
            <>
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined />}
                onClick={onPrevious}
                disabled={currentInfoIndex === 0}
                style={{
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                  minWidth: 24,
                  padding: '0 4px',
                }}
              />
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                onClick={() => onNext(filteredInfos.length)}
                disabled={currentInfoIndex === filteredInfos.length - 1}
                style={{
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                  minWidth: 24,
                  padding: '0 4px',
                }}
              />
            </>
          )}
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
              minWidth: 24,
              padding: '0 4px',
            }}
          />
        </div>
      </div>
      {/* Content */}
      <div style={{
        fontSize: 11,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        fontWeight: 500,
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
      }}>
        {currentInfo.info_text}
      </div>
      {filteredInfos.length > 1 && (
        <div style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          fontSize: 10,
          fontWeight: 500,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
          textAlign: 'center',
        }}>
          {currentInfoIndex + 1} / {filteredInfos.length}
        </div>
      )}
    </div>
  );
};
